
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const buildUrl = (path, params) => {
  const base = API_BASE;
  const url = `${base}${path}`;
  if (!params || Object.keys(params).length === 0) return url;
  const qs = new URLSearchParams(params).toString();
  return `${url}?${qs}`;
};

const request = async (path, { method = 'GET', data, params, headers } = {}) => {
  const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
  const h = headers ? { ...headers } : {};
  if (!isForm && data !== undefined && !h['Content-Type']) h['Content-Type'] = 'application/json';

  const res = await fetch(buildUrl(path, params), {
    method,
    credentials: 'include',
    headers: h,
    body: (method === 'GET' || method === 'HEAD')
      ? undefined
      : (isForm ? data : (data !== undefined ? JSON.stringify(data) : undefined))
  });

  const status = res.status;
  if (status === 401 && typeof window !== 'undefined') {
    if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
      window.location.href = '/admin/login';
    }
  }

  const ct = res.headers.get('content-type') || '';
  let parsed;
  if (ct.includes('application/json')) {
    try { parsed = await res.json(); } catch { parsed = null; }
  } else {
    parsed = await res.text();
  }

  if (!res.ok) {
    const message = parsed && parsed.message ? parsed.message : (typeof parsed === 'string' && parsed ? parsed : 'Request failed');
    const err = new Error(message);
    err.status = status;
    throw err;
  }

  return { data: parsed, status };
};

const client = {
  get: (path, config = {}) => request(path, { method: 'GET', params: config.params, headers: config.headers }),
  delete: (path, config = {}) => request(path, { method: 'DELETE', params: config.params, headers: config.headers }),
  post: (path, data, config = {}) => request(path, { method: 'POST', data, params: config.params, headers: config.headers }),
  put: (path, data, config = {}) => request(path, { method: 'PUT', data, params: config.params, headers: config.headers }),
  patch: (path, data, config = {}) => request(path, { method: 'PATCH', data, params: config.params, headers: config.headers }),
};

const resp = p => p.then(r => r.data);

const api = {
  users: {
    login: (data) => resp(client.post('/users/login', data)),
    logout: () => resp(client.post('/users/logout')),
    me: () => resp(client.get('/users/me')),
    list: () => resp(client.get('/users')),
    create: (data) => resp(client.post('/users', data)),
    updatePassword: (data) => resp(client.patch('/users/password', data)),
    setActive: (data) => resp(client.patch('/users/status', data)),
  },
  home: {
    get: () => resp(client.get('/home')),
    update: (data) => resp(client.put('/home', data)),
    deleteHeroImage: (index) => resp(client.delete(`/home/hero/${index}`))
  },
  about: {
    get: () => resp(client.get('/about')),
    update: (data) => resp(client.put('/about', data)),
    deleteImage: (field) => resp(client.delete(`/about/image`, { params: { field } }))
  },
  objectives: {
    get: () => resp(client.get('/objectives')),
    update: (data) => resp(client.put('/objectives', data)),
    deleteObjectiveImage: (index) => resp(client.delete(`/objectives/objective/${index}/image`)),
    deleteSectionImage: (index) => resp(client.delete(`/objectives/section/${index}/image`)),
  },
  news: {
    getHeader: () => resp(client.get('/news')),
    updateHeader: (data) => resp(client.put('/news', data)),
    listPosts: (params) => resp(client.get('/news-posts', { params })),
    createPost: (data) => resp(client.post('/news-posts', data)),
    getPost: (id) => resp(client.get(`/news-posts/${id}`)),
    updatePost: (id, data) => resp(client.put(`/news-posts/${id}`, data)),
    deletePost: (id) => resp(client.delete(`/news-posts/${id}`)),
    deletePostImage: (id, index) => resp(client.delete(`/news-posts/${id}/images/${index}`))
  },
  team: {
    getPage: () => resp(client.get('/team')),
    updatePage: (data) => resp(client.put('/team', data)),
    listMembers: () => resp(client.get('/team/members')),
    createMember: (data) => resp(client.post('/team/members', data)),
    getMember: (id) => resp(client.get(`/team/members/${id}`)),
    updateMember: (id, data) => resp(client.put(`/team/members/${id}`, data)),
    deleteMember: (id) => resp(client.delete(`/team/members/${id}`))
  },
  gallery: {
    getPage: () => resp(client.get('/gallery/page')),
    updatePage: (data) => resp(client.put('/gallery/page', data)),
    list: () => resp(client.get('/gallery')),
    create: (data) => resp(client.post('/gallery', data)),
    bulk: (items) => resp(client.post('/gallery/bulk', { items })),
    update: (id, data) => resp(client.put(`/gallery/${id}`, data)),
    delete: (id) => resp(client.delete(`/gallery/${id}`))
  },
  partnerships: {
    getPage: () => resp(client.get('/partnerships')),
    updatePage: (data) => resp(client.put('/partnerships', data)),
  },
  partners: {
    list: () => resp(client.get('/partners')),
    create: (data) => resp(client.post('/partners', data)),
    update: (id, data) => resp(client.put(`/partners/${id}`, data)),
    delete: (id) => resp(client.delete(`/partners/${id}`))
  },
  contact: {
    getPage: () => resp(client.get('/contact')),
    updatePage: (data) => resp(client.put('/contact', data)),
    createMessage: (data) => resp(client.post('/contact/messages', data)),
    listMessages: () => resp(client.get('/contact/messages'))
  },
  services: {
    getPage: () => resp(client.get('/services')),
    updatePage: (data) => resp(client.put('/services', data))
  },
  foodPremises: {
    getContent: () => resp(client.get('/food-premises/content')),
    updateContent: (data) => resp(client.put('/food-premises/content', data)),
    apply: (data) => resp(client.post('/food-premises/apply', data)),
    getMyApplication: () => resp(client.get('/food-premises/my-application')),
    getAllApplications: () => resp(client.get('/food-premises/applications')),
    reviewApplication: (id, data) => resp(client.put(`/food-premises/applications/${id}`, data)),
    createAccount: (data) => resp(client.post('/food-premises/create-account', data))
  },
  careers: {
    getPage: () => resp(client.get('/careers/page')),
    updatePage: (data) => resp(client.put('/careers/page', data)),
    listJobs: () => resp(client.get('/careers/jobs')),
    getJob: (id) => resp(client.get(`/careers/jobs/${id}`)),
    createJob: (data) => resp(client.post('/careers/jobs', data)),
    updateJob: (id, data) => resp(client.put(`/careers/jobs/${id}`, data)),
    deleteJob: (id) => resp(client.delete(`/careers/jobs/${id}`)),
    submitApplication: (data) => resp(client.post('/careers/applications', data)),
    listApplications: (params) => resp(client.get('/careers/applications', { params })),
    updateApplicationStatus: (id, data) => resp(client.patch(`/careers/applications/${id}`, data)),
    deleteApplication: (id) => resp(client.delete(`/careers/applications/${id}`))
  },
  faAct: {
    get: () => resp(client.get('/fa-act')),
    update: (data) => resp(client.put('/fa-act', data))
  },
  upload: {
    image: (file) => {
      const formData = new FormData();
      formData.append('image', file);
      return resp(client.post('/upload/image', formData));
    },
    pdf: (file) => {
      const formData = new FormData();
      formData.append('pdf', file);
      return resp(client.post('/upload/pdf', formData));
    }
  }
};

export default api;
